import { useCallback, useSyncExternalStore } from 'react';
import { GROUP_ORDER, type NavGroup } from '../consts/navigation';

/**
 * Preferencia de secciones colapsadas del menu, persistida en `localStorage`
 * por usuario. Es un store a nivel de modulo (no `useState` local) para que
 * las dos instancias de `SidebarContent` — sidebar desktop y drawer mobile —
 * compartan el mismo valor y se re-rendericen juntas al togglear, y para que
 * un cambio en otra pestaña se propague.
 *
 * `getSnapshot` re-lee `localStorage` en cada llamada pero devuelve la MISMA
 * referencia mientras el contenido no cambie (comparacion por valor), como
 * exige `useSyncExternalStore` para no entrar en loop de render.
 */

const KEY_PREFIX = 'mirasoles.nav.collapsedGroups.';
const EMPTY: NavGroup[] = [];

const listeners = new Set<() => void>();
/** Ultimo array devuelto por key, para conservar identidad referencial. */
const lastSnapshot: Record<string, NavGroup[]> = {};
/**
 * Listener global de `storage` (sincronizacion entre pestañas). Se engancha
 * cuando aparece el primer suscriptor y se desengancha cuando se va el ultimo,
 * para no dejar el handler colgado si el arbol se desmonta (tests, HMR).
 */
let storageListener: ((e: StorageEvent) => void) | null = null;

function keyFor(userId: string | undefined): string {
  return KEY_PREFIX + (userId ?? 'anon');
}

function readFromStorage(key: string): NavGroup[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    const valid = parsed.filter(
      (g): g is NavGroup => typeof g === 'string' && (GROUP_ORDER as string[]).includes(g),
    );
    return valid.length ? valid : EMPTY;
  } catch {
    // localStorage inaccesible (modo privado) o JSON corrupto → sin preferencia.
    return EMPTY;
  }
}

function sameGroups(a: NavGroup[], b: NavGroup[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function getSnapshot(key: string): NavGroup[] {
  const fresh = readFromStorage(key);
  const prev = lastSnapshot[key];
  if (prev && sameGroups(prev, fresh)) return prev;
  lastSnapshot[key] = fresh;
  return fresh;
}

function setGroups(key: string, next: NavGroup[]): void {
  lastSnapshot[key] = next;
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* no se puede persistir (modo privado, cuota) — el estado en memoria igual se aplica */
  }
  listeners.forEach((l) => l());
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  if (!storageListener && typeof window !== 'undefined') {
    storageListener = (e: StorageEvent) => {
      if (e.key && e.key.startsWith(KEY_PREFIX)) {
        listeners.forEach((l) => l());
      }
    };
    window.addEventListener('storage', storageListener);
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0 && storageListener && typeof window !== 'undefined') {
      window.removeEventListener('storage', storageListener);
      storageListener = null;
    }
  };
}

export function useCollapsedNavGroups(userId: string | undefined) {
  const key = keyFor(userId);
  const collapsed = useSyncExternalStore(
    subscribe,
    () => getSnapshot(key),
    () => EMPTY,
  );

  const toggle = useCallback(
    (group: NavGroup) => {
      const current = getSnapshot(key);
      setGroups(
        key,
        current.includes(group)
          ? current.filter((g) => g !== group)
          : [...current, group],
      );
    },
    [key],
  );

  return { collapsed, toggle };
}
